import KatexRenderer from '../../../utils/KatexRenderer'
/**
 * ExplanationPrint - Print renderer for essay/explanation quizzes
 * Layout: PrintQuizTitle → image → lined answer area
 */

import type { ReactNode } from 'react'
import { cn } from '../../../lib/utils'
import { formatProblem } from '../../../utils/quiz-parser'
import { KatexText } from '../shared'
import type { BaseQuizData } from '../../../types/legacyQuiz'
import { PRINT_CORRECT_ANSWER_CLASS, PrintQuizTitle, PrintQuizImage, getStyles, getImageUrl, getPrintScale } from './print-common'

interface ExplanationPrintProps {
  quizId: string | number
  quiz: BaseQuizData
  number: number
  showCorrectAnswers?: boolean
  correctAnswers?: string[]
  compact?: boolean
}

export function ExplanationPrint({
  quizId,
  quiz,
  number,
  showCorrectAnswers = false,
  correctAnswers,
  compact,
}: ExplanationPrintProps) {
  const s = getStyles(compact)
  const imageUrl = getImageUrl(quiz.image, quiz.imagePrint)
  const printScale = getPrintScale(quiz.image, quiz.imagePrint)
  const renderedProblem: ReactNode[] = quiz.problem
    ? formatProblem(
        quiz.problem,
        `${quizId}-print-problem`,
        () => null,
        quiz.additionalImages ?? undefined,
      )
    : []

  return (
    <div className={cn(s.container, s.quizSection)}>
      {/* Title */}
      <PrintQuizTitle number={number} compact={compact}>
        <KatexText text={quiz.title} />
      </PrintQuizTitle>

      {/* Image */}
      {imageUrl && <PrintQuizImage src={imageUrl} printScale={printScale} />}

      {/* Problem — 모범답안 마커를 제외한 문제 본문 */}
      {renderedProblem.length > 0 && (
        <div className={s.problemArea}>
          <div
            data-quiz-problem-text
            className={cn(
              s.problemLine,
              compact
                ? 'text-[15px] text-black tracking-[-0.09px] font-normal leading-[26px]'
                : s.problemText,
            )}
          >
            {renderedProblem}
          </div>
        </div>
      )}

      {/* Answer area - lined box for writing */}
      <div className={s.problemArea}>
        <div
          className="w-full min-h-[120px] border-[1.5px] border-[#ccc] border-solid rounded-[6px] bg-white"
          style={{
            backgroundImage: 'repeating-linear-gradient(to bottom, transparent, transparent 31px, #e5e5e5 31px, #e5e5e5 32px)',
            backgroundPosition: '0 8px',
          }}
        >
          {showCorrectAnswers && correctAnswers && correctAnswers.length > 0 && (
            <div className={cn('p-[8px] text-[14px]', PRINT_CORRECT_ANSWER_CLASS)}>
              <KatexRenderer text={correctAnswers.join(', ')} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
